import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Database, CheckCircle, Circle, Play, FileText, Upload, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import api from "../../lib/api";


const MigrationPage = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [targetTenant, setTargetTenant] = useState("default");
  const [uploads, setUploads] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, uploadsRes, tenantsRes] = await Promise.all([
        api.get("/migration/status"),
        api.get("/migration/uploads"),
        api.get("/tenants").catch(() => ({ data: { tenants: [] } })),
      ]);
      setStatus(statusRes.data);
      setUploads(uploadsRes.data.uploads || []);
      if (tenantsRes.data?.tenants) setTenants(tenantsRes.data.tenants);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleChecklist = async (key) => {
    try {
      await api.post("/migration/checklist/${key}", {});
      fetchData();
    } catch (e) { toast.error("Failed to update checklist"); }
  };

  const runDryRun = async () => {
    setRunning(true);
    try {
      await api.post("/migration/dry-run", {});
      toast.success("Dry run completed");
      fetchData();
    } catch (e) { toast.error("Dry run failed"); }
    setRunning(false);
  };

  const runValidation = async () => {
    setValidating(true);
    try {
      const res = await api.post("/migration/validate", {});
      setValidation(res.data);
      if (res.data.is_valid) toast.success("Validation passed - ready to import");
      else toast.error("Validation found errors - fix CSV data before importing");
    } catch (e) { toast.error(e.response?.data?.detail || "Validation failed"); }
    setValidating(false);
  };

  const runImport = async () => {
    setRunning(true);
    try {
      const res = await api.post("/migration/import?target_tenant_id=${targetTenant}", {});
      const r = res.data.results;
      toast.success(`Import complete: ${r.total_imported} imported, ${r.total_skipped} skipped`);
      setValidation(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setRunning(false);
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>;

  const checklist = status?.checklist || [];
  const completedItems = checklist.filter(c => c.done).length;
  const report = status?.last_run;

  return (
    <AdminLayout title="Migration">
    <div className="p-6 space-y-6" data-testid="migration-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Absorb LMS Migration</h1>
        <p className="text-sm text-muted-foreground mt-1">Migrate data from Absorb LMS with ETL pipeline, validation, and audit reporting</p>
      </div>

      {/* Progress */}
      <div className="bg-background border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Migration Status</h2>
              <p className="text-sm text-muted-foreground">{completedItems}/{checklist.length} checklist items completed</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-bold uppercase ${status?.status === 'not_started' ? 'bg-muted text-muted-foreground' : status?.status === 'dry_run_complete' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
            {(status?.status || "not_started").replace(/_/g, " ")}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${checklist.length ? (completedItems / checklist.length * 100) : 0}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Checklist */}
        <div className="bg-background border rounded-xl p-5 shadow-sm">
          <h3 className="font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>Pre-Migration Checklist</h3>
          <div className="space-y-3">
            {checklist.map(item => (
              <button key={item.key} onClick={() => toggleChecklist(item.key)} className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`checklist-${item.key}`}>
                {item.done ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                <span className={`text-sm ${item.done ? 'line-through text-muted-foreground' : 'font-medium'}`}>{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <div className="bg-background border rounded-xl p-5 shadow-sm">
            <h3 className="font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>Step 1: Upload CSV Files</h3>
            <div className="space-y-3">
              {["users", "courses", "completions"].map(dtype => {
                const uploaded = uploads.find(u => u.data_type === dtype);
                return (
                  <div key={dtype} className="relative">
                    <input type="file" accept=".csv" className="hidden" id={`upload-${dtype}`}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append("file", file);
                        try {
                          const res = await api.post(`/migration/upload-csv?data_type=${dtype}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
                          toast.success(`Uploaded ${res.data.row_count} ${dtype} rows`);
                          setValidation(null);
                          fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                        e.target.value = "";
                      }} data-testid={`upload-${dtype}`} />
                    <Button variant="outline" className="w-full justify-start h-12" onClick={() => document.getElementById(`upload-${dtype}`).click()} data-testid={`upload-btn-${dtype}`}>
                      <Upload className="w-5 h-5 mr-3" /> Upload {dtype.charAt(0).toUpperCase() + dtype.slice(1)} CSV
                      {uploaded && <span className="ml-auto text-xs text-green-600 font-medium">{uploaded.row_count} rows</span>}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-background border rounded-xl p-5 shadow-sm">
            <h3 className="font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>Step 2: Validate & Import</h3>
            <div className="space-y-3">
              {/* Target Tenant Selector */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">Target Tenant</label>
                <select
                  value={targetTenant}
                  onChange={(e) => setTargetTenant(e.target.value)}
                  className="w-full h-10 px-3 border rounded-lg bg-background text-sm"
                  data-testid="target-tenant-select"
                >
                  <option value="default">Default Tenant</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <Button variant="outline" className="w-full justify-start h-12" onClick={runValidation} disabled={validating || uploads.length === 0} data-testid="validate-btn">
                {validating ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <ShieldCheck className="w-5 h-5 mr-3" />}
                {validating ? "Validating..." : "Validate Uploaded Data"}
              </Button>

              <Button variant="outline" className="w-full justify-start h-12" onClick={runDryRun} disabled={running} data-testid="run-dry-run-btn">
                <Play className="w-5 h-5 mr-3" /> {running ? "Running..." : "Run Dry Run (Staging)"}
              </Button>

              <Button className="w-full justify-start h-12 bg-blue-600 hover:bg-blue-700 text-white" onClick={runImport} disabled={running || uploads.length === 0} data-testid="run-import-btn">
                {running ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <Database className="w-5 h-5 mr-3" />}
                {running ? "Importing..." : "Run Production Import"}
              </Button>
            </div>
          </div>

          {/* Validation Results */}
          {validation && (
            <div className={`bg-background border rounded-xl p-5 shadow-sm ${validation.is_valid ? 'border-green-300' : 'border-red-300'}`} data-testid="validation-results">
              <h3 className="font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                {validation.is_valid ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-red-600" />}
                Validation {validation.is_valid ? "Passed" : "Failed"}
              </h3>
              <div className="space-y-2 text-sm">
                {Object.entries(validation.data_types || {}).map(([dt, info]) => (
                  <div key={dt} className="p-2 bg-muted/50 rounded">
                    <span className="font-medium capitalize">{dt}</span>: {info.row_count} rows
                    {info.errors?.length > 0 && <span className="text-red-600 ml-2">{info.errors.length} error(s)</span>}
                    {info.errors?.map((err, i) => <p key={i} className="text-xs text-red-500 mt-1">{err}</p>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Last Run Report */}
          {report && (
            <div className="bg-background border rounded-xl p-5 shadow-sm" data-testid="migration-report">
              <h3 className="font-bold mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>Last Import Report</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><span className="font-medium capitalize">{report.type}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status:</span><span className="font-medium capitalize">{report.status}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Users:</span><span>{report.results?.users_imported_count || 0} imported / {report.results?.users_skipped_count || 0} skipped / {report.results?.users_source_count || 0} total</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Courses:</span><span>{report.results?.courses_imported_count || 0} imported / {report.results?.courses_skipped_count || 0} skipped / {report.results?.courses_source_count || 0} total</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Completions:</span><span>{report.results?.completions_imported_count || 0} imported / {report.results?.completions_skipped_count || 0} skipped / {report.results?.completions_source_count || 0} total</span></div>
                {report.results?.errors?.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                    <p className="font-medium text-red-800 mb-1">Errors ({report.results.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {report.results.errors.slice(0, 10).map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}
                      {report.results.errors.length > 10 && <p className="text-xs text-red-400">...and {report.results.errors.length - 10} more</p>}
                    </div>
                  </div>
                )}
                {report.note && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {report.note}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default MigrationPage;
